import FilterDrawer from 'components/Assignments/FilterDrawer';
import { features } from 'config/features';
import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useJoyride } from 'contexts/joyride';
import { useSnackbar } from 'contexts/snackbar';
import { useViewport } from 'contexts/viewport';
import { omit } from 'lodash';
import Desktop from 'pages/InstructorAssignments/desktop';
import Mobile from 'pages/InstructorAssignments/mobile';
import { PageContext } from 'pages/InstructorAssignments/PageProvider';
import Tablet from 'pages/InstructorAssignments/tablet';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import assignmentService from 'services/assignment';
import courseService from 'services/course';
import { Button, Loading, Stack, Text } from 'shell/components';
import { isAssignmentValid } from 'utils/helper';
import { v4 as uuid } from 'uuid';

const PageLayout = () => {
    const { isLoading, course, setAssignments } = useApp();
    const { auth, currentUser } = useAuth();
    const { setDialog, closeDialog } = useDialog();
    const history = useHistory();
    const { joyrideRun, setJoyrideRun, setJoyridePaused, joyrideStepIndex, setJoyrideStepIndex, performNext } =
        useJoyride();
    const { message, close } = useSnackbar();
    const { device } = useViewport();
    const { t } = useTranslation('assignment');

    const { filters, setFilters, resetFilters } = useContext(PageContext);

    const [isDuplicating, setIsDuplicating] = useState(false);

    const getFilterCount = (filters) => {
        let count = 0;
        if (filters.sort !== 'none') count++;
        return count;
    };

    const onClickCreate = () => {
        if (joyrideRun) return performNext();
        history.push('/assignments/new');
    };

    const onClick = ({ id }) => {
        if (joyrideRun) {
            if (joyrideStepIndex === 11) {
                history.push(`/assignments/${id}`);
                setJoyrideRun(false);
                return;
            }
            return performNext();
        }
        history.push(`/assignments/${id}`);
    };

    const onClickEdit = ({ id }) => {
        history.push(`/assignments/${id}/edit`);
    };

    const onClickDuplicate = async (assignment) => {
        setIsDuplicating(true);
        const timestamp = new Date().getTime();
        const duplicate = {
            ...omit(assignment, ['_id', 'is_rag_ready']),
            id: uuid(),
            owner_account_id: auth.id,
            created_on: timestamp,
            modified_on: timestamp,
            title: t('duplicate_title', 'Copy of {{title}}', { title: assignment.title }),
            is_draft: true,
            has_seen_preview: false,
            has_finished_preview: !features.enableFinetuning, // initialize as true when fine tuning is hidden
            preview_files: [],
        };
        await courseService.createCourseAssignment(course.id, duplicate);
        setAssignments((current) => [...current, duplicate]);
        message.success(
            <Stack alignItems={'center'} direction={'row'} spacing={4}>
                <Text color={'inherit'} variant={'p1'}>
                    {t('message.duplicate-success', 'Your assignment was successfully duplicated.')}
                </Text>
                <Button
                    color={'neutral'}
                    size={'large'}
                    variant={'borderless'}
                    onClick={() => {
                        history.push(`/assignments/${duplicate.id}`);
                        close();
                    }}
                >
                    {t('common:controls.open', 'Open')}
                </Button>
            </Stack>
        );
        setIsDuplicating(false);
    };

    const onClickUnpublish = (assignment) => {
        setDialog({
            open: true,
            message: t('dialog.unpublish-assignment.title', 'Unpublish assignment?'),
            description: t(
                'dialog.unpublish-assignment.description',
                'Are you sure you want to unpublish this assignment?'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.unpublish', 'Unpublish'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: async () => {
                const timestamp = new Date().getTime();
                setAssignments((current) => {
                    return current.map((currentAssignment) => {
                        if (currentAssignment.id !== assignment.id) return currentAssignment;
                        return {
                            ...currentAssignment,
                            modified_on: timestamp,
                            is_draft: true,
                        };
                    });
                });
                closeDialog();
                message.success(t('message.update-success', 'Your assignment was successfully updated.'));
                await assignmentService.updateAssignment(assignment.id, {
                    ...omit(assignment, ['_id']),
                    modified_on: timestamp,
                    is_draft: true,
                });
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const onClickPublish = (assignment) => {
        if (!isAssignmentValid(assignment)) {
            return setDialog({
                open: true,
                message: t('dialog.required-information-incomplete.title', 'Required Information Incomplete'),
                description: t(
                    'dialog.required-information-incomplete.description',
                    'Before you can publish this assignment, please edit it and add any missing required information.'
                ),
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.ok', 'Ok'),
                cancelButtonText: '',
                onAction: () => closeDialog(),
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        }

        setDialog({
            open: true,
            message: t('dialog.publish-assignment.title', 'Publish assignment?'),
            description: t(
                'dialog.publish-assignment.description',
                'Are you sure you want to publish this assignment?'
            ),
            note: '',
            variant: 'primary',
            actionButtonText: t('common:controls.publish', 'Publish'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: async () => {
                const timestamp = new Date().getTime();
                setAssignments((current) => {
                    return current.map((currentAssignment) => {
                        if (currentAssignment.id !== assignment.id) return currentAssignment;
                        return {
                            ...currentAssignment,
                            modified_on: timestamp,
                            is_draft: false,
                        };
                    });
                });
                closeDialog();
                message.success(t('message.update-success', 'Your assignment was successfully updated.'));
                await assignmentService.updateAssignment(assignment.id, {
                    ...omit(assignment, ['_id']),
                    modified_on: timestamp,
                    is_draft: false,
                });
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const onClickDelete = (assignment) => {
        setDialog({
            open: true,
            message: t('dialog.delete-assignment.title', 'Delete Assignment?'),
            description: t('dialog.delete-assignment.description', 'This action cannot be undone.'),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.delete', 'Delete'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: async () => {
                setAssignments((current) => current.filter(({ id }) => id !== assignment.id));
                closeDialog();
                message.success(t('message.delete-success', 'Your assignment was successfully deleted.'));
                await assignmentService.deleteAssignment(assignment.id);
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    useEffect(() => {
        if (currentUser?.finished_onboarding || isLoading) return;
        if (joyrideStepIndex < 2) {
            setJoyrideStepIndex(2);
            setJoyrideRun(true);
        }
    }, [currentUser?.finished_onboarding, isLoading, joyrideStepIndex, setJoyrideRun, setJoyrideStepIndex]);

    useEffect(() => {
        setJoyridePaused(false);
        return () => setJoyridePaused(true);
    }, [setJoyridePaused]);

    const props = {
        isDuplicating,
        filters,
        setFilters,
        getFilterCount,
        onClickCreate,
        onClick,
        onClickEdit,
        onClickDuplicate,
        onClickUnpublish,
        onClickPublish,
        onClickDelete,
    };

    const renderResponsiveLayout = (device) => {
        return {
            mobile: <Mobile {...props} />,
            tablet: <Tablet {...props} />,
            desktop: <Desktop {...props} />,
        }[device];
    };

    return isLoading ? (
        <Loading />
    ) : (
        <>
            {renderResponsiveLayout(device)}
            <FilterDrawer filters={filters} setFilters={setFilters} resetFilters={resetFilters} />
        </>
    );
};

export default PageLayout;
